import { ContactItem } from '@AMIEWEB/Common';
import { ContactEditor } from '@AMIEWEB/Placement/PlacementDetails/PlacementDetailsHeader/helper';
import { Avatar, Box, Divider, Grid, List, ListItem, ListItemAvatar, ListItemText, Tooltip } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useMemo, useState } from 'react';
import MailBoxIcon from 'app/assets/images/MailBox.svg';
import PhoneIcon from 'app/assets/images/Phone.svg';
import { Name2 } from 'app/models/Candidate/CandidateProfile';
import { useTranslation } from 'react-i18next';
import EmailDisabled from 'app/assets/images/CandidateHeader/email-Disabled.svg';
import PhoneDisabled from 'app/assets/images/CandidateHeader/PhoneDisabled.svg';
import { useEnableEmailCommunication, useEnableVoiceCommunication } from 'oidc/CommunicationEnabler';
import { useHeaderOptions } from '@AMIEWEB/Common/Buttons/UseHeaderOptions';
import { searchNotificationData } from '@AMIEWEB/GlobalSearch/helper';
import { ChannelType, SendType, UseType } from '@AMIEWEB/Notification/Constants';
import { EmailDefaultValues } from 'app/models/Email/IEmail';
import { IEmployee, IToken } from 'app/models/Notification/Notification';
import _ from 'lodash';
import { selectUser } from 'oidc/user.selectors';
import { useSelector, useDispatch } from 'react-redux';
import { notificationDataActions } from 'store/redux-store/notification/notification.redux';
import { notificationSelection } from 'store/redux-store/notification/notification.selector';
import { Concatenate } from 'utils/string/string';
import { getDefaultSender } from '@AMIEWEB/Candidate/CandidateProfile/CandidateHeader/NotificationHelper';
import { Authorized } from 'oidc/userHelper';
import { userRoles } from 'oidc/userRoles';
import { getSearchLookups } from 'app/services/SharedServices/SharedServices';
import { updateOrderKeyContact } from 'app/services/OrderServices/OrderServices';
import { httpSuccess } from 'app/services/serviceHelpers';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { orderDataActions } from '@AMIEWEB/Order/Store/Order.redux';
import { updateOrderViewAccountManager } from '@AMIEWEB/Order/OrderDetails/helper';
import { trackPromise } from 'react-promise-tracker';

const useStyles = makeStyles()(theme => ({
  drawer: {
    flexShrink: 0,
    '& .MuiPaper-root': {
      top: '45px',
      width: '420px',
    },
    '& .MuiListItem-gutters': {
      paddingLeft: '0px',
      paddingRight: '0px',
    },
    '& .MuiListItemAvatar-root': {
      minWidth: '0px',
    },
  },
  userAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '80px',
    opacity: 1,
    color: 'white',
    backgroundColor: '#52429A',
  },
  root: {
    padding: '0px 10px',
    width: '100%',
    backgroundColor: '#fff',
  },
  accountManagerRoot: {
    padding: '0px 10px',
    width: '100%',
    backgroundColor: theme.palette.framework.system.backgroundLightGrey,
  },
  userAvatarPad: {
    paddingRight: '8px',
  },
  actionIconPad: {
    paddingLeft: '8px',
  },
  primaryText: {
    fontSize: '16px',
    color: '#003E68',
  },
  secondaryText: {
    fontSize: '12px',
    color: '#333333',
  },
  tooltip: {
    display: 'grid',
  },
  tooltipArrow: {
    color: '#fff',
    '&::before': {
      border: '1px solid #555555',
    },
  },
  tooltipBackground: {
    backgroundColor: '#fff',
    color: '#555555',
    border: '1px solid #555555',
    fontSize: '12px',
  },
}));

interface IOrderTab {
  contactDetails: any[];
  orderId?: string;
}

const ACCOUNT_MANAGER_CONTACT_NAME = 'Account Manager';

export const OrderTab = (props: IOrderTab) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { contactDetails, orderId } = props;
  const dispatch = useDispatch();
  const userData = useSelector(selectUser);
  const orderDetailsView = useSelector(selectOrderDetailsUIView);

  const enableEmailCommunication = useEnableEmailCommunication();
  const enableVoiceCommunication = useEnableVoiceCommunication();

  const isAccountManagerEditAuthorized = Authorized(
    [userRoles.accountManagement_Leadership, userRoles.accountManagement_TeamMember, userRoles.amieTeam],
    userData.userInfo,
  );

  const accountManagerContact = contactDetails?.find(data => data.contactName === ACCOUNT_MANAGER_CONTACT_NAME);

  const [editAccountManager, setEditAccountManager] = useState({
    value: null as { name: string; value: number; id: number } | null,
    error: null as { message: string } | null,
    isDirty: false,
    helperText: undefined as string | undefined,
  });

  useEffect(() => {
    const contactId = accountManagerContact?.contactId;
    const title = Concatenate([accountManagerContact?.name?.firstName, accountManagerContact?.name?.lastName], ' ');
    setEditAccountManager(prev => ({
      ...prev,
      value: contactId ? { name: title, value: contactId, id: contactId } : null,
      error: null,
      isDirty: false,
      helperText: prev.helperText === 'Saved' ? prev.helperText : undefined,
    }));
  }, [accountManagerContact?.contactId, accountManagerContact?.name?.firstName, accountManagerContact?.name?.lastName]);

  const displayNullData = data => {
    if (data.firstName === undefined && data.lastName === undefined) {
      return true;
    } else return false;
  };

  const genInitials = (name: Name2) => {
    return `${name?.firstName?.substring(0, 1) || ''}${name?.lastName?.substring(0, 1) || 'UNK'}`;
  };

  const ignoreLookupSearchForSpecialCharacter = (searchKey: string) => {
    const lastCharacter = searchKey.slice(-1);
    const specialCharacterPattern = /[!@#$%^&*()_+[\]{};:'"\\|,.<>/?]/;
    return specialCharacterPattern.test(lastCharacter);
  };

  const fetchOptions = async (key: string) => {
    if (ignoreLookupSearchForSpecialCharacter(key)) return;
    return await getSearchLookups('accountmanagers', key);
  };

  const onAccountManagerChange = newValue => {
    const value = newValue?.id ? newValue : newValue?.value;
    setEditAccountManager(prev => ({
      ...prev,
      value,
      error: null,
      isDirty: value?.id !== accountManagerContact?.contactId,
      helperText: undefined,
    }));
  };

  const onAccountManagerCancel = () => {
    const contactId = accountManagerContact?.contactId;
    const title = Concatenate([accountManagerContact?.name?.firstName, accountManagerContact?.name?.lastName], ' ');
    setEditAccountManager({
      value: contactId ? { name: title, value: contactId, id: contactId } : null,
      error: null,
      isDirty: false,
      helperText: undefined,
    });
  };

  const handleSaveAccountManager = async value => {
    if (!orderId || !orderDetailsView) return;

    const response = await trackPromise(
      updateOrderKeyContact({
        orderId: parseInt(orderId, 10),
        keyContact: 1,
        employeeId: parseInt(value.id, 10),
        userId: userData.userInfo?.employeeId ?? 0,
        amieOrdersTimestamp: orderDetailsView.orderDetails.amieOrdersTimestamp,
      }),
      'save-contact-accountManager',
    );

    if (httpSuccess(response?.status)) {
      const [firstName, ...lastNameParts] = (value.name || '').split(' ');
      const lastName = lastNameParts.join(' ');
      const updatedAccountManager =
        response?.data && response.data.firstName
          ? {
              userId: response.data.userId ?? response.data.id ?? value.id,
              firstName: response.data.firstName,
              lastName: response.data.lastName,
              email: response.data.email,
              phoneNumber: response.data.phoneNumber,
            }
          : {
              userId: value.id,
              firstName,
              lastName,
            };

      setEditAccountManager({
        value: { name: value.name, value: value.id, id: value.id },
        error: null,
        isDirty: false,
        helperText: 'Saved',
      });

      setTimeout(() => {
        setEditAccountManager(prev => ({ ...prev, helperText: undefined }));
        dispatch(
          orderDataActions.setOrderDetailsView(updateOrderViewAccountManager(orderDetailsView, updatedAccountManager)),
        );
      }, 2000);
    } else {
      setEditAccountManager(prev => ({
        ...prev,
        error: { message: 'Error. Try again' },
      }));
    }
  };

  const accountManagerItem = useMemo(
    () => ({
      designation: ACCOUNT_MANAGER_CONTACT_NAME,
      keyAttribute: 'accountManager',
      title: Concatenate([accountManagerContact?.name?.firstName, accountManagerContact?.name?.lastName], ' '),
      avatarText: genInitials(accountManagerContact?.name),
      phoneNumber: accountManagerContact?.phoneNumber,
      email: accountManagerContact?.email,
      id: accountManagerContact?.contactId,
      hideMessageIcon: true,
      lockReason: t('global.accessDisabled'),
      disabled: !isAccountManagerEditAuthorized,
    }),
    [accountManagerContact, isAccountManagerEditAuthorized, t],
  );

  const accountManagerEditor = useMemo(
    () => (
      <ContactEditor
        {...accountManagerItem}
        {...editAccountManager}
        fetchOptions={fetchOptions}
        onApply={handleSaveAccountManager}
        onChange={onAccountManagerChange}
        onCancel={onAccountManagerCancel}
      />
    ),
    [accountManagerItem, editAccountManager],
  );

  const ContactList = props => {
    const { userInfo } = useSelector(selectUser);
    const notificationData = useSelector(notificationSelection);
    const dispatch = useDispatch();
    const { getEmailSubject } = useHeaderOptions();
    const handleCommunication = (args: { channelType: ChannelType; contactData: any }) => {
      const notificationUser = searchNotificationData({ userInfo });
      const { contactData } = args;
      const tos = {
        designation: contactData?.title,
        contactId: contactData?.contactId,
        phoneNumber: contactData?.phoneNumber,
        email: contactData?.email,
        name: contactData?.name?.firstName + ' ' + contactData?.name?.lastName,
      };
      const associatedRecords = [
        {
          name: 'partnerDesignation',
          value: contactData?.title,
        },
        {
          name: 'userId',
          value: contactData?.contactId,
        },
      ];
      const sender: IEmployee = getDefaultSender({ userInfo });
      if (args.channelType === ChannelType.voice) {
        const VoiceData = {
          manualLog: false,
          channel: args.channelType,
          associatedRecords: associatedRecords,
          sender: sender,
          sendType: SendType.one_to_one,
          tos: [tos],
          useType: UseType.Placement,
          userRole: Concatenate(userInfo?.roles || [], ','),
          isInternal: true,
        };
        if (!notificationData.voice.open && notificationData.voice.minimized) {
          dispatch(
            notificationDataActions.setVoiceInteraction({
              open: true,
              minimized: false,
              showDisposition: true,
            }),
          );
          if (notificationData.voice.data.tos.find(Boolean).contactId !== args.contactData?.id) {
            dispatch(notificationDataActions.setSnackBarData({ channel: 'voice', manual: false, changeWarning: true }));
          }
        } else {
          dispatch(
            notificationDataActions.setVoiceData({
              data: VoiceData,
              open: true,
              minimized: false,
            }),
          );
        }
      } else {
        const currentUrl = window.location.href?.split('?')[0];
        const body = `<p>&nbsp;</p><p>&nbsp;</p><p>Link To Profile : <a href=${currentUrl}>${currentUrl}</a><br><br>{{signature}}</p>`;
        const substitutions: Array<IToken> = [
          {
            name: 'Signature',
            input: '{{signature}}',
          },
        ];
        if (!notificationData.email.open && notificationData.email.minimized) {
          dispatch(
            notificationDataActions.setEmailInteraction({
              open: true,
              minimized: false,
            }),
          );
          if (!_.isEqual(notificationData.email.data!.tos, [])) {
            dispatch(notificationDataActions.setSnackBarData({ channel: 'email', manual: false, changeWarning: true }));
          }
        } else {
          dispatch(
            notificationDataActions.setEmailData({
              data: {
                ...EmailDefaultValues(),
                ...notificationUser,
                substitutions: substitutions,
                useType: UseType.Placement,
                emailType: SendType.one_to_one,
                associatedRecords: associatedRecords,
                body: body,
                isInternal: true,
                subject: getEmailSubject(),
                tos: [tos],
              },
              open: true,
              minimized: false,
            }),
          );
        }
      }
    };
    return (
      <>
        <Grid item className={classes.root}>
          <Grid item className={`${classes.drawer} `}>
            <List>
              <ListItem disableGutters>
                {!displayNullData(props.name) ? (
                  <>
                    <ListItemAvatar className={classes.userAvatarPad}>
                      <Avatar data-avatar className={classes.userAvatar}>
                        {genInitials(props.name)}
                      </Avatar>
                    </ListItemAvatar>

                    <ListItemText
                      primary={`${props?.name?.firstName || ''} ${props?.name?.middleName || ''} ${
                        props?.name?.lastName || ''
                      }`}
                      secondary={props.title}
                      classes={{ primary: classes.primaryText, secondary: classes.secondaryText }}
                    />

                    <ListItemAvatar className={classes.actionIconPad}>
                      {enableEmailCommunication && !!props?.email && props?.email !== '' ? (
                        <Tooltip
                          title={
                            <span className={classes.tooltip}>
                              <span>{props?.email || ''}</span>{' '}
                            </span>
                          }
                          disableHoverListener={!props?.email}
                          arrow
                          placement="bottom-end"
                          classes={{ tooltip: classes.tooltipBackground, arrow: classes.tooltipArrow }}
                        >
                          <img
                            src={MailBoxIcon}
                            style={{ cursor: 'pointer' }}
                            onClick={() =>
                              handleCommunication({ channelType: ChannelType.email, contactData: { ...props } })
                            }
                            alt="MailBox"
                          />
                        </Tooltip>
                      ) : (
                        <img src={EmailDisabled} alt="Email disabled" />
                      )}
                    </ListItemAvatar>
                    <ListItemAvatar className={classes.actionIconPad}>
                      {enableVoiceCommunication && !!props?.phoneNumber && props?.phoneNumber !== '' ? (
                        <Tooltip
                          title={
                            <span className={classes.tooltip}>
                              <span>
                                {props.countryDialingCode || '+1 '}
                                {props?.phoneNumber || ''}
                              </span>{' '}
                            </span>
                          }
                          disableHoverListener={!props?.phoneNumber}
                          arrow
                          placement="bottom-end"
                          classes={{ tooltip: classes.tooltipBackground, arrow: classes.tooltipArrow }}
                        >
                          <img
                            src={PhoneIcon}
                            style={{ cursor: 'pointer' }}
                            alt="Phone"
                            onClick={() =>
                              handleCommunication({ channelType: ChannelType.voice, contactData: { ...props } })
                            }
                          />
                        </Tooltip>
                      ) : (
                        <img src={PhoneDisabled} alt="Phone disabled" />
                      )}
                    </ListItemAvatar>
                  </>
                ) : (
                  <b>{t('candidate.myPlacements.placementContactsDrawer.NA')}</b>
                )}
              </ListItem>
            </List>
          </Grid>
          <Grid item>{props.index < contactDetails.length - 1 ? <Divider /> : ''}</Grid>
        </Grid>
      </>
    );
  };

  const renderAccountManagerContact = (index: number) => {
    if (!accountManagerContact) {
      return null;
    }

    if (!isAccountManagerEditAuthorized) {
      return (
        <ContactList
          title={accountManagerContact.contactName}
          name={accountManagerContact.name}
          index={index}
          email={accountManagerContact.email}
          phoneNumber={accountManagerContact.phoneNumber}
          contactId={accountManagerContact.contactId}
        />
      );
    }

    return (
      <>
        <Grid item className={classes.accountManagerRoot}>
          <ContactItem
            {...accountManagerItem}
            title={accountManagerEditor}
            enableCommunication
            disableMailIcon={editAccountManager?.isDirty || false}
            disablePhoneIcon={editAccountManager?.isDirty || false}
            disableMessageIcon={editAccountManager?.isDirty || false}
            disableAvatar={editAccountManager?.isDirty || !editAccountManager?.value || false}
          />
        </Grid>
        {index < contactDetails.length - 1 && (
          <Grid item className={classes.root}>
            <Divider />
          </Grid>
        )}
      </>
    );
  };

  return (
    <>
      <Grid container classes={{ container: classes.root }}>
        {contactDetails?.map((data, index) => {
          if (data.contactName === ACCOUNT_MANAGER_CONTACT_NAME) {
            return <React.Fragment key={data.contactName}>{renderAccountManagerContact(index)}</React.Fragment>;
          }

          return (
            <ContactList
              key={data.contactName}
              title={data.contactName}
              name={data.name}
              index={index}
              email={data.email}
              phoneNumber={data.phoneNumber}
              contactId={data.contactId}
            />
          );
        })}
      </Grid>

      <Box className={classes.root}></Box>
    </>
  );
};
